import { Stack } from '@mui/material';
import { useRecoilValue } from 'recoil';
import {
  ExpandableContentButton,
  ExpandableContentWrapper,
} from 'components/shared';
import { CanvasGroupLinkButton } from 'components/canvas/groups';
import { CanvasOptionsCalculatorProvider } from 'hooks/optionsCalculator/CanvasOptionsCalculatorProvider';
import OptionsCalculatorContainer from 'pages/components/OptionsCalculatorContainer';
import { hiroSymbolsState } from 'states/hiro';
import { CanvasContentId, CanvasComponentConfig } from 'types/canvas';
import { OptionsCalculatorCanvasConfig } from 'types/optionsCalculator';
import CanvasSymSelector from './CanvasSymSelector';

interface CanvasOptionsCalculatorProps {
  id: number;
  sym: string;
  config: CanvasComponentConfig;
  onSymChange: (sym: string) => void;
  onConfigChange: (patch: Partial<CanvasComponentConfig>) => void;
}

/**
 * Full options calculator — ticker overview, strategy picker, PnL chart,
 * and saved-position management — dropped into a canvas widget. It's the
 * same `OptionsCalculatorContainer` the standalone page renders; only the
 * state source underneath differs (see `CanvasOptionsCalculatorProvider`).
 */
const CanvasOptionsCalculator = ({
  id,
  sym,
  config,
  onSymChange,
  onConfigChange,
}: CanvasOptionsCalculatorProps) => {
  const hiroSymbols = useRecoilValue(hiroSymbolsState);
  const symOptions = Array.from(hiroSymbols)
    .sort()
    .map((s) => ({ symbol: s }));

  const expandableContentButtonType = `${CanvasContentId.OPTIONS_CALCULATOR}-${id}`;

  return (
    <CanvasOptionsCalculatorProvider
      sym={sym}
      onSymChange={onSymChange}
      config={config as OptionsCalculatorCanvasConfig}
      onConfigChange={onConfigChange}
    >
      <ExpandableContentWrapper
        type={expandableContentButtonType}
        styleOverrides={{ p: 1, height: '100%', overflowY: 'auto' }}
      >
        <Stack
          direction="column"
          spacing={1}
          sx={{ height: '100%', width: '100%' }}
        >
          <Stack
            sx={{
              px: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
            }}
          >
            <CanvasSymSelector
              sym={sym}
              options={symOptions}
              onSymChange={onSymChange}
            />
            <CanvasGroupLinkButton
              componentId={id}
              contentId={CanvasContentId.OPTIONS_CALCULATOR}
            />
            <ExpandableContentButton type={expandableContentButtonType} />
          </Stack>
          <OptionsCalculatorContainer enableGlobalSymbolSearch={false} />
        </Stack>
      </ExpandableContentWrapper>
    </CanvasOptionsCalculatorProvider>
  );
};

export default CanvasOptionsCalculator;
